import React, {ReactElement} from "react";
import {BaseApplet, BaseAppletProps, BaseAppletState} from "../BaseApplet";
import {$KTS, Action, ACTION_SPACE, KeyTextStrings, ProfilePhoto, UserDisplayName} from "../../../shared/types";
import {AddOutlined} from "@mui/icons-material";
import {EditPatientHelper} from "./EditPatientHelper";
import {PageFragment, PageFragmentProps, PageFragmentState, PageFragmentStyleFlags} from "../../../shared/PageFragment";
import {Patient, Patients} from "../../../zyrcona-shared/types";
import {TableDataContainer, TableDataRow} from "../../../shared/TableDataContainer";
import {TableData} from "../../../shared/tabledata";
import {BaseApp} from "../../../shared/BaseApp";
import {TableDataSourceFragmentProps, TableDataSourceFragmentState} from "../../../shared/TableDataSourceFragment";
import {ListTableDataSourceFragment} from "../../../shared/ListTableDataSourceFragment";
import {DateUtil, FORMAT_SHOW_YEAR} from "../../../shared/date_util";
import {AppletTabsContainer} from "../AppletTabsContainer";
import {FormGenContainer, FormGenContainerMode} from "../../../shared/FormGenContainer";
import {Card, Typography} from "@mui/material";

export type PatientsAppletProps = BaseAppletProps & {}

type PatientsAppletState = BaseAppletState & {
  patientId?: string,
}

export class PatientsApplet extends BaseApplet<PatientsAppletProps, PatientsAppletState> {

  protected getTitle(): string | null | undefined {
    return "Patients";
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return this.renderToolbarButtonsInToolbarContainerEx([
      ACTION_SPACE,
      new Action("New Patient", () => EditPatientHelper.addPatient(), AddOutlined).setVariant("contained"),
    ]);
  }

  protected renderContainerContent(): React.ReactElement | null {
    if (this.state.patientId) {
      return <PatientFragment patientId={this.state.patientId} onBack={() => this.setState({patientId: null})}/>;
    }
    return super.renderContainerContent();
  }

  protected renderApplet(): React.ReactElement {
    return <PatientsFragment path={this.props.path} onSelected={patientId => this.setState({patientId})}/>;
  }
}

class PatientsTableDataRow implements TableDataRow {

  @TableData({name: "", type: "profile_photo"})
  profilePhoto?: string;

  @TableData({name: "Name"})
  name: string;

  @TableData({name: "Short code", cellSize: "medium"})
  shortcode: string;

  @TableData({name: "Date of Birth", cellSize: "large"})
  dateOfBirth: string;

  @TableData({
    name: "Actions",
    type: "actions",
    actions: (event, args) => [
      new Action("Delete", () => {
        BaseApp.CONTEXT.showTextDialog(
          `Delete ${UserDisplayName((args.data as Patient).member.user)}`,
          "Are you sure you want to delete this patient? This action cannot be undone.",
          new Action("Confirm", () => {
          }));
      }).makeDestructive(),
    ],
  })
  actions: void;
}

export type PatientsFragmentProps = TableDataSourceFragmentProps & {
  onSelected: (patientId: string) => void,
}

type PatientsFragmentState = TableDataSourceFragmentState & {}

export class PatientsFragment extends ListTableDataSourceFragment<Patient, PatientsTableDataRow, Patients, PatientsFragmentProps, PatientsFragmentState> {

  constructor(props: PatientsFragmentProps, context: any) {
    super(props, context, Patients.getInstance());
  }

  createTableDataRow(): PatientsTableDataRow {
    return new PatientsTableDataRow();
  }

  applyTableDataToRow(data: Patient, content: PatientsTableDataRow): void {
    content.profilePhoto = ProfilePhoto(data.basicInfo.profilePhoto);
    content.name = data.displayName();
    content.shortcode = data.shortcode;
    content.dateOfBirth = DateUtil.formatDateTime(data.basicInfo.dateOfBirth, FORMAT_SHOW_YEAR);
  }

  onTableDataSelected(data: Patient) {
    this.props.onSelected(data.id);
  }

  onTableCellChanged(data: Patient, cellId: string, value: any): void {
  }

  renderContent(): ReactElement {
    const patients = this.loader.getListItems();
    return <TableDataContainer
      path={this.props.path}
      data={(from, to) => patients.slice(from, to)}
      dataTotal={patients.length}
      source={this}
      multiSelectListener={this}/>;
  }
}

type PatientFragmentProps = PageFragmentProps & {
  patientId: string,
  onBack: () => void,
}

enum PatientTab {
  INFO = "info",
  CASES = "cases",
  ORDERS = "orders",
}

const PATIENT_TAB_KTS = new KeyTextStrings([
  $KTS(PatientTab.INFO, "Info"),
  $KTS(PatientTab.CASES, "Cases"),
  $KTS(PatientTab.ORDERS, "Orders"),
]);

type PatientFragmentState = PageFragmentState & {
  patient?: Patient,
  patientTab: PatientTab,
}

class PatientFragment extends PageFragment<PatientFragmentProps, PatientFragmentState> {

  protected onCreateState(props?: PatientFragmentProps): PatientFragmentState {
    return {
      ...super.onCreateState(props),
      patientTab: PatientTab.INFO,
    };
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_TOOLBAR_TYPE_FLAG | PatientFragment.STYLE_BACK_BUTTON_FLAG;
  }

  onBackButtonClicked() {
    this.props.onBack();
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const patient = await Patients.getInstance().getOrLoadItem(this.props.patientId);
    this.setState({
      patient: patient,
      title: patient?.displayName(),
    });
  }

  renderContent(): React.ReactElement {
    if (!this.state.patient) {
      return <Typography>Patient not found.</Typography>
    }
    return <AppletTabsContainer kts={PATIENT_TAB_KTS} onSelect={key => this.setState({patientTab: key as PatientTab})}>
      {this.renderPatientTab()}
    </AppletTabsContainer>
  }

  private renderPatientTab(): ReactElement {
    switch (this.state.patientTab) {
      case PatientTab.INFO:
        return <Card style={{display: "flex", flexDirection: "column"}}>
          <FormGenContainer mode={FormGenContainerMode.READ} content={this.state.patient}/>
        </Card>
    }
  }
}